import * as React from "react";
import { SidebarNav, NAV, GROUP_MAP } from "@/components/design-system/SidebarNav";
import { ColorsSection } from "@/components/design-system/sections/branding/ColorsSection";
import { TypographySection } from "@/components/design-system/sections/branding/TypographySection";
import { ShellsSection } from "@/components/design-system/sections/structure/ShellsSection";
import { MainNavSection } from "@/components/design-system/sections/structure/MainNavSection";
import { SubNavSection } from "@/components/design-system/sections/structure/SubNavSection";
import { PageHeadersSection } from "@/components/design-system/sections/structure/PageHeadersSection";
import { BodyContentSection } from "@/components/design-system/sections/structure/BodyContentSection";
import { FootersSection } from "@/components/design-system/sections/structure/FootersSection";
import { BaseStylesSection } from "@/components/design-system/sections/base-styles/BaseStylesSection";
import { IconographySection } from "@/components/design-system/sections/elements/IconographySection";
import { ButtonsSection } from "@/components/design-system/sections/elements/ButtonsSection";
import { FormsSection } from "@/components/design-system/sections/elements/FormsSection";
import { BadgesSection } from "@/components/design-system/sections/elements/BadgesSection";
import { ToggleButtonsSection } from "@/components/design-system/sections/elements/ToggleButtonsSection";
import { ListingsSection } from "@/components/design-system/sections/elements/ListingsSection";
import { ChartSection } from "@/components/design-system/sections/elements/ChartSection";
import { ModalSection } from "@/components/design-system/sections/elements/ModalSection";
import { BottomSheetSection } from "@/components/design-system/sections/elements/BottomSheetSection";
import { MobileDataListSection } from "@/components/design-system/sections/elements/MobileDataListSection";
import { CalloutSection } from "@/components/design-system/sections/elements/CalloutSection";
import { AlertSection } from "@/components/design-system/sections/elements/AlertSection";
import { DropdownMenuSection } from "@/components/design-system/sections/elements/DropdownMenuSection";
import { ChatSection } from "@/components/design-system/sections/elements/ChatSection";

export function DesignSystem({ section }: { section: "branding" | "structure" | "elements" | "base-styles" }) {
  const [activeId, setActiveId] = React.useState<string>("");
  const badgesContainerRef = React.useRef<HTMLDivElement>(null);

  const filteredNav = React.useMemo(() => {
    const targetLabel = GROUP_MAP[section];
    return NAV.filter((group) => group.label === targetLabel);
  }, [section]);

  React.useEffect(() => {
    const ids = filteredNav.flatMap((g) => g.items.map((i) => i.id));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [filteredNav]);

  // Center active badge on mobile view
  React.useEffect(() => {
    if (!activeId || !badgesContainerRef.current) return;
    const activeBadge = badgesContainerRef.current.querySelector(
      `[data-badge-id="${activeId}"]`
    );
    if (activeBadge) {
      activeBadge.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeId]);

  const handleBadgeClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const navItems = React.useMemo(() => {
    return filteredNav.flatMap((g) => g.items);
  }, [filteredNav]);

  return (
    <div className="flex h-full w-full overflow-hidden bg-page text-ink-body">
      <aside className="hidden w-64 shrink-0 border-r border-hairline lg:block">
        <div className="h-full overflow-y-auto p-4">
          <SidebarNav activeSection={section} activeId={activeId} />
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-y-auto scroll-smooth px-4 py-8 sm:px-8 lg:px-12">
        <div className="mb-12 max-w-2xl">
          <h1 className="text-3xl font-semibold text-ink-display">
            Design System
          </h1>
          <p className="mt-2 text-ink-muted">
            Single source of truth for every visual primitive in this app.
            Reuse what's here. Propose additions before building one-offs.
          </p>

          {/* Mobile sub-navigation */}
          {navItems.length > 0 && (
            <div
              ref={badgesContainerRef}
              className="no-scrollbar -mx-4 mt-6 flex gap-2 overflow-x-auto px-4 pb-3 pt-1 snap-x scroll-smooth lg:hidden"
            >
              {navItems.map((item) => {
                const isActive = activeId === item.id;
                return (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    data-badge-id={item.id}
                    onClick={(e) => handleBadgeClick(e, item.id)}
                    className={[
                      "inline-flex shrink-0 snap-center items-center justify-center rounded-full px-3.5 py-1.5 text-sm font-medium border no-underline transition-all duration-200",
                      isActive
                        ? "bg-accent-faded border-accent/30 text-accent-display shadow-sm scale-102"
                        : "bg-surface border-hairline text-ink-body hover:border-ink-muted/30 hover:bg-surface/80"
                    ].join(" ")}
                  >
                    {item.label}
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {section === "branding" && (
          <>
            <ColorsSection />
            <TypographySection />
          </>
        )}

        {section === "structure" && (
          <>
            <ShellsSection />
            <MainNavSection />
            <SubNavSection />
            <PageHeadersSection />
            <BodyContentSection />
            <FootersSection />
          </>
        )}

        {section === "elements" && (
          <>
            <IconographySection />
            <ButtonsSection />
            <FormsSection />
            <BadgesSection />
            <ToggleButtonsSection />
            <ListingsSection />
            <ChartSection />
            <ModalSection />
            <BottomSheetSection />
            <MobileDataListSection />
            <DropdownMenuSection />
            <CalloutSection />
            <AlertSection />
            <ChatSection />
          </>
        )}

        {section === "base-styles" && (
          <>
            <BaseStylesSection />
          </>
        )}
      </main>
    </div>
  );
}

export default DesignSystem;
